import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, ReactNode } from 'react';
import { doc, getDoc, deleteDoc, collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase';
import { useAuth } from './AuthContext';
import { useLicense } from './LicenseContext';
import { logAudit } from '../utils/audit';
import {
  BreweryDoc,
  BreweryMember,
  BreweryInvitation,
  ViewPermissions,
  ViewPermission,
} from '../types';

interface BreweryContextType {
  // Brewery info
  brewery: BreweryDoc | null;
  breweryId: string | null;
  breweryLoading: boolean;

  // Current user's membership
  currentMember: BreweryMember | null;
  currentPermissions: ViewPermissions;

  // Permission checks
  hasViewAccess: (viewKey: string) => boolean;
  hasWriteAccess: (viewKey: string) => boolean;

  // Members (admin only)
  members: BreweryMember[];
  membersLoading: boolean;

  // Invitations (admin only)
  invitations: BreweryInvitation[];
  invitationsLoading: boolean;

  // Actions
  inviteMember: (email: string, permissions: { views: ViewPermissions }) => Promise<{ emailSent: boolean }>;
  resendInvitation: (invitationId: string) => Promise<void>;
  cancelInvitation: (invitationId: string) => Promise<void>;
  updatePermissions: (memberId: string, permissions: { views: ViewPermissions }) => Promise<void>;
  removeMember: (memberId: string) => Promise<void>;
  syncBreweryData: () => Promise<any>;

  // Sync state
  isSyncing: boolean;
  lastSyncResults: any | null;
  /** Server-side sync timestamp from syncState/current; null if never synced */
  lastSyncedAt: Date | null;
  /** Only brewery admins may trigger the brewerySync Cloud Function */
  canSync: boolean;
  syncError: string | null;
}

const BreweryContext = createContext<BreweryContextType | undefined>(undefined);

interface BreweryProviderProps {
  children: ReactNode;
}

export const BreweryProvider: React.FC<BreweryProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const { breweryId, isBreweryAdmin, isBreweryMember } = useLicense();

  const [brewery, setBrewery] = useState<BreweryDoc | null>(null);
  const [breweryLoading, setBreweryLoading] = useState(true);
  const [currentMember, setCurrentMember] = useState<BreweryMember | null>(null);
  const [members, setMembers] = useState<BreweryMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(true);
  const [invitations, setInvitations] = useState<BreweryInvitation[]>([]);
  const [invitationsLoading, setInvitationsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncResults, setLastSyncResults] = useState<any | null>(null);
  const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null);
  const [syncStateLoaded, setSyncStateLoaded] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  // Deduplicate concurrent sync requests (three data contexts share one button press)
  const syncInFlightRef = useRef<Promise<any> | null>(null);
  // Auto-trigger the first sync only once per session
  const autoSyncFiredRef = useRef(false);
  // Whether the admin has a Brewfather key stored on their user doc. brewerySync
  // uses this key (breweries/{id}/apiKey is never populated), so the auto-sync
  // trigger must gate on it — not on the always-empty brewery-doc key.
  const [adminHasApiKey, setAdminHasApiKey] = useState(false);

  // Listen to brewery document
  useEffect(() => {
    if (!breweryId) {
      setBrewery(null);
      setBreweryLoading(false);
      return;
    }

    const breweryRef = doc(db, 'breweries', breweryId);
    const unsubscribe = onSnapshot(
      breweryRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          setBrewery(docSnapshot.data() as BreweryDoc);
        } else {
          setBrewery(null);
        }
        setBreweryLoading(false);
      },
      (error) => {
        console.error('Error listening to brewery doc:', error);
        setBreweryLoading(false);
      }
    );

    return () => unsubscribe();
  }, [breweryId]);

  // Listen to current user's member document
  useEffect(() => {
    if (!breweryId || !user?.uid) {
      setCurrentMember(null);
      return;
    }

    const memberRef = doc(db, 'breweries', breweryId, 'members', user.uid);
    const unsubscribe = onSnapshot(
      memberRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          setCurrentMember(docSnapshot.data() as BreweryMember);
        } else {
          setCurrentMember(null);
        }
      },
      (error) => {
        console.error('Error listening to member doc:', error);
      }
    );

    return () => unsubscribe();
  }, [breweryId, user?.uid]);

  // Listen to all members (admin only)
  useEffect(() => {
    if (!breweryId || !isBreweryAdmin) {
      setMembers([]);
      setMembersLoading(false);
      return;
    }

    const membersRef = collection(db, 'breweries', breweryId, 'members');
    const unsubscribe = onSnapshot(
      membersRef,
      (snapshot) => {
        const membersList: BreweryMember[] = [];
        snapshot.forEach((doc) => {
          membersList.push({ ...doc.data(), id: doc.id } as BreweryMember & { id: string });
        });
        setMembers(membersList);
        setMembersLoading(false);
      },
      (error) => {
        console.error('Error listening to members:', error);
        setMembersLoading(false);
      }
    );

    return () => unsubscribe();
  }, [breweryId, isBreweryAdmin]);

  // Listen to invitations (admin only)
  useEffect(() => {
    if (!breweryId || !isBreweryAdmin) {
      setInvitations([]);
      setInvitationsLoading(false);
      return;
    }

    const invitationsRef = collection(db, 'breweries', breweryId, 'invitations');
    const unsubscribe = onSnapshot(
      invitationsRef,
      (snapshot) => {
        const invitationsList: BreweryInvitation[] = [];
        snapshot.forEach((doc) => {
          invitationsList.push({ ...doc.data(), id: doc.id } as BreweryInvitation);
        });
        setInvitations(invitationsList);
        setInvitationsLoading(false);
      },
      (error) => {
        console.error('Error listening to invitations:', error);
        setInvitationsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [breweryId, isBreweryAdmin]);

  // Permission checks
  const currentPermissions: ViewPermissions = useMemo(
    () => currentMember?.permissions?.views || {},
    [currentMember]
  );

  const hasViewAccess = useCallback((viewKey: string): boolean => {
    if (isBreweryAdmin) return true;
    const perm = currentPermissions[viewKey];
    return perm?.access ?? false;
  }, [isBreweryAdmin, currentPermissions]);

  // The write tier is hidden for now: no small-brewery use case for a
  // read-only member emerged, and the flag was barely enforced. Access implies
  // write. The stored {access, write} shape is untouched — to re-enable the
  // tier, restore `perm?.access && perm?.write` here and the Write column in
  // BreweryUsers.tsx.
  const hasWriteAccess = useCallback((viewKey: string): boolean => {
    if (isBreweryAdmin) return true;
    const perm = currentPermissions[viewKey];
    return perm?.access ?? false;
  }, [isBreweryAdmin, currentPermissions]);

  // Actions
  const inviteMember = useCallback(async (email: string, permissions: { views: ViewPermissions }) => {
    const functions = getFunctions();
    const createInvitation = httpsCallable(functions, 'createBreweryInvitation');
    const result = await createInvitation({ email, permissions });
    const emailSent = !!(result.data as { emailSent?: boolean })?.emailSent;
    if (breweryId && user) {
      logAudit(breweryId, {
        action: 'member_invited',
        target: 'invitations',
        summary: `Invited ${email}${emailSent ? '' : ' (email delivery failed)'}`,
      }, user);
    }
    return { emailSent };
  }, [breweryId, user]);

  // Firestore rules already restrict invitation writes to the brewery admin,
  // so this needs no Cloud Function. Cancelling frees the seat it reserved.
  const cancelInvitation = useCallback(async (invitationId: string) => {
    if (!breweryId) return;
    await deleteDoc(doc(db, 'breweries', breweryId, 'invitations', invitationId));
    if (user) {
      logAudit(breweryId, {
        action: 'invitation_cancelled', target: 'invitations', targetId: invitationId,
        summary: 'Cancelled pending invitation',
      }, user);
    }
  }, [breweryId, user]);

  const resendInvitation = useCallback(async (invitationId: string) => {
    const functions = getFunctions();
    const resend = httpsCallable(functions, 'resendBreweryInvitation');
    await resend({ invitationId });
    if (breweryId && user) {
      logAudit(breweryId, {
        action: 'invitation_resent', target: 'invitations', targetId: invitationId,
        summary: 'Resent invitation email',
      }, user);
    }
  }, [breweryId, user]);

  const updatePermissions = useCallback(async (memberId: string, permissions: { views: ViewPermissions }) => {
    const functions = getFunctions();
    const updatePerms = httpsCallable(functions, 'updateMemberPermissions');
    await updatePerms({ memberId, permissions });
    if (breweryId && user) {
      logAudit(breweryId, { action: 'permissions_changed', target: 'members', targetId: memberId, summary: `Changed permissions for member ${memberId}` }, user);
    }
  }, [breweryId, user]);

  const removeMember = useCallback(async (memberId: string) => {
    const functions = getFunctions();
    const remove = httpsCallable(functions, 'removeBreweryMember');
    await remove({ memberId });
    if (breweryId && user) {
      logAudit(breweryId, { action: 'member_removed', target: 'members', targetId: memberId, summary: `Removed member ${memberId}` }, user);
    }
  }, [breweryId, user]);

  // Server-side sync timestamps. This is the source of truth for "Last synced"
  // in brewery mode — snapshot arrival times only say when the listener fired.
  useEffect(() => {
    if (!breweryId) {
      setLastSyncedAt(null);
      setSyncStateLoaded(false);
      return;
    }
    const syncStateRef = doc(db, 'breweries', breweryId, 'syncState', 'current');
    const unsubscribe = onSnapshot(
      syncStateRef,
      (snap) => {
        const ts = snap.exists() ? snap.data()?.lastBatchSync : null;
        setLastSyncedAt(ts?.toDate ? ts.toDate() : null);
        setSyncStateLoaded(true);
      },
      (error) => {
        console.error('Error listening to syncState:', error);
        setSyncStateLoaded(true);
      }
    );
    return () => unsubscribe();
  }, [breweryId]);

  const syncBreweryData = useCallback(async () => {
    // One CF call even when several contexts/views request a sync at once
    if (syncInFlightRef.current) return syncInFlightRef.current;

    const run = (async () => {
      setIsSyncing(true);
      setSyncError(null);
      try {
        const functions = getFunctions();
        const sync = httpsCallable(functions, 'brewerySync');
        const result = await sync({});
        setLastSyncResults((result.data as any).results);
        if (breweryId && user) {
          logAudit(breweryId, { action: 'sync_triggered', target: 'syncState', summary: 'Triggered Brewfather data sync' }, user);
        }
        return (result.data as any).results;
      } catch (error: any) {
        setSyncError(error?.message || 'Brewfather sync failed');
        throw error;
      } finally {
        setIsSyncing(false);
        syncInFlightRef.current = null;
      }
    })();

    syncInFlightRef.current = run;
    return run;
  }, [breweryId, user]);

  // Any brewery account can trigger a sync — the CF runs it with the admin's
  // Brewfather credentials, so members can refresh instead of waiting on the admin.
  const canSync = isBreweryAdmin || isBreweryMember;

  // Detect whether the admin has a Brewfather key on their user doc (the key
  // brewerySync actually uses). breweryId === the admin's own uid.
  useEffect(() => {
    if (!isBreweryAdmin || !user?.uid) { setAdminHasApiKey(false); return; }
    let cancelled = false;
    getDoc(doc(db, 'users', user.uid))
      .then((snap) => { if (!cancelled) setAdminHasApiKey(!!snap.data()?.apiKey); })
      .catch(() => { if (!cancelled) setAdminHasApiKey(false); });
    return () => { cancelled = true; };
  }, [isBreweryAdmin, user?.uid]);

  // First-run fetch: a brewery that has never synced (no syncState timestamp)
  // has no data in Firestore and nothing will push it — trigger the initial
  // sync automatically once the admin's API key is configured.
  useEffect(() => {
    if (!isBreweryAdmin || !breweryId) return;
    if (!syncStateLoaded || lastSyncedAt !== null) return;
    if (!brewery?.apiKey && !adminHasApiKey) return; // no key yet — Settings will prompt for it
    if (autoSyncFiredRef.current) return;
    autoSyncFiredRef.current = true;
    console.log('BreweryContext: no sync timestamp found, running initial Brewfather sync');
    syncBreweryData().catch((error) => {
      console.error('Initial brewery sync failed:', error);
    });
  }, [isBreweryAdmin, breweryId, syncStateLoaded, lastSyncedAt, brewery?.apiKey, adminHasApiKey, syncBreweryData]);

  const value: BreweryContextType = {
    brewery,
    breweryId,
    breweryLoading,
    currentMember,
    currentPermissions,
    hasViewAccess,
    hasWriteAccess,
    members,
    membersLoading,
    invitations,
    invitationsLoading,
    inviteMember,
    resendInvitation,
    cancelInvitation,
    updatePermissions,
    removeMember,
    syncBreweryData,
    isSyncing,
    lastSyncResults,
    lastSyncedAt,
    canSync,
    syncError,
  };

  return (
    <BreweryContext.Provider value={value}>
      {children}
    </BreweryContext.Provider>
  );
};

export const useBrewery = (): BreweryContextType => {
  const context = useContext(BreweryContext);
  if (context === undefined) {
    throw new Error('useBrewery must be used within a BreweryProvider');
  }
  return context;
};

// Non-throwing variant for the data contexts (Batch/Recipe/Inventory), which
// render both with and without a BreweryProvider above them.
export const useOptionalBrewery = (): BreweryContextType | undefined => useContext(BreweryContext);
